/** Invalidation only: product data and credentials never travel between tabs. */
const channelName = 'longjie-catalog-changes';
const storageKey = 'longjie-catalog-change';
const listeners = new Set<() => void>();
const seen = new Set<string>();
let channel: BroadcastChannel | undefined;
let scheduled: ReturnType<typeof setTimeout> | undefined;
let shouldBroadcast = false;

type CatalogChange = { type: 'catalog-changed'; id: string };

function remember(id: string) {
  seen.add(id);
  if (seen.size > 128) seen.delete(seen.values().next().value!);
}

function openChannel() {
  try {
    return typeof BroadcastChannel === 'undefined' ? undefined : new BroadcastChannel(channelName);
  } catch { return undefined; }
}

function broadcast() {
  const change: CatalogChange = {
    type: 'catalog-changed',
    id: typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
  };
  remember(change.id);
  const publisher = channel ?? openChannel();
  try { publisher?.postMessage(change); }
  catch { /* Storage remains available if a browser blocks BroadcastChannel. */ }
  finally { if (publisher !== channel) publisher?.close(); }
  try { window.localStorage.setItem(storageKey, JSON.stringify(change)); }
  catch { /* Same-page subscribers and BroadcastChannel do not require storage. */ }
}

function schedule(broadcastChange: boolean) {
  shouldBroadcast ||= broadcastChange;
  if (scheduled !== undefined) return;
  scheduled = setTimeout(() => {
    scheduled = undefined;
    const publish = shouldBroadcast;
    shouldBroadcast = false;
    if (publish) broadcast();
    for (const listener of [...listeners]) listener();
  }, 100);
}

function receive(value: unknown) {
  if (!value || typeof value !== 'object') return;
  const change = value as Partial<CatalogChange>;
  if (change.type !== 'catalog-changed' || typeof change.id !== 'string'
    || !change.id || change.id.length > 128 || seen.has(change.id)) return;
  remember(change.id);
  schedule(false);
}

function onStorage(event: StorageEvent) {
  if (event.key !== storageKey || !event.newValue) return;
  try { receive(JSON.parse(event.newValue)); }
  catch { /* Ignore malformed or unrelated storage messages. */ }
}

/** Refresh the subscribing surface after a successful catalog write in any tab. */
export function subscribeCatalogChanges(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  if (!listeners.size) {
    window.addEventListener('storage', onStorage);
    channel = openChannel();
    if (channel) channel.onmessage = (event) => receive(event.data);
  }
  // Keep independent subscriptions independent even when passed the same callback.
  const subscription = () => listener();
  listeners.add(subscription);
  return () => {
    listeners.delete(subscription);
    if (listeners.size) return;
    window.removeEventListener('storage', onStorage);
    channel?.close();
    channel = undefined;
    seen.clear();
    // A confirmed local write must still reach other tabs after this view unmounts.
    if (!shouldBroadcast) {
      clearTimeout(scheduled);
      scheduled = undefined;
    }
  };
}

/** The API calls this only after a successful response has been consumed. */
export function publishCatalogChangeForRequest(path: string, method = 'GET'): void {
  if (typeof window === 'undefined' || !['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase())) return;
  let pathname: string;
  try { pathname = new URL(path, window.location.href).pathname; }
  catch { return; }
  if (!/^\/api\/v1\/admin\/(?:products|skus|categories|code-categories)(?:\/|$)/.test(pathname)
    && !/^\/api\/v1\/admin\/imports\/[^/]+\/confirm\/?$/.test(pathname)) return;
  schedule(true);
}
